import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, EyeOff, Wand2 } from 'lucide-react';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams({
        paid_tier_price: settings.paid_tier_price,
        pdf_download_price: settings.pdf_download_price,
        free_tier_album_limit: settings.free_tier_album_limit,
        default_ai_instructions: settings.default_ai_instructions || ''
      });
      
      if (settings.facebook_app_id) {
        params.append('facebook_app_id', settings.facebook_app_id);
      }
      if (settings.facebook_app_secret) {
        params.append('facebook_app_secret', settings.facebook_app_secret);
      }
      
      await axios.put(`${API}/admin/settings?${params.toString()}`);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      <header className="bg-white/60 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <h2 className="text-xl font-semibold mb-6">Pricing & Tier Settings</h2>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="free-limit">Free Tier Album Limit</Label>
              <Input
                id="free-limit"
                type="number"
                value={settings.free_tier_album_limit}
                onChange={(e) => setSettings({ ...settings, free_tier_album_limit: parseInt(e.target.value) })}
                min="1"
              />
              <p className="text-sm text-gray-500">Maximum number of albums for free users (photos are unlimited)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paid-price">Subscription Price (USD/year)</Label>
              <Input
                id="paid-price"
                type="number"
                step="0.01"
                value={settings.paid_tier_price}
                onChange={(e) => setSettings({ ...settings, paid_tier_price: parseFloat(e.target.value) })}
                min="0"
              />
              <p className="text-sm text-gray-500">Annual subscription for unlimited albums</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pdf-price">PDF Download Price (USD)</Label>
              <Input
                id="pdf-price"
                type="number"
                step="0.01"
                value={settings.pdf_download_price}
                onChange={(e) => setSettings({ ...settings, pdf_download_price: parseFloat(e.target.value) })}
                min="0"
              />
              <p className="text-sm text-gray-500">One-time fee for free users to download album as PDF</p>
            </div>
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-amber-600 to-rose-500 text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-gray-200 mt-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            Default AI Instructions
          </h2>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="ai-instructions">AI Photo Analysis Instructions</Label>
              <Textarea
                id="ai-instructions"
                value={settings.default_ai_instructions || ''}
                onChange={(e) => setSettings({ ...settings, default_ai_instructions: e.target.value })}
                rows={8}
                className="font-mono text-sm"
                placeholder="Enter default AI instructions for new albums..."
              />
              <p className="text-sm text-gray-500">
                These instructions will be used as the default for all new albums. Users can customize them per album.
              </p>
            </div>
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-amber-600 to-rose-500 text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-gray-200 mt-6">
          <h2 className="text-xl font-semibold mb-6">Facebook Integration</h2>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fb-app-id">Facebook App ID</Label>
              <Input
                id="fb-app-id"
                type="text"
                placeholder="1770510201018530"
                value={settings.facebook_app_id || ''}
                onChange={(e) => setSettings({ ...settings, facebook_app_id: e.target.value })}
              />
              <p className="text-xs text-gray-600">Get this from Facebook Developers dashboard</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fb-app-secret">Facebook App Secret</Label>
              <div className="relative">
                <Input
                  id="fb-app-secret"
                  type={showSecret ? "text" : "password"}
                  placeholder="Enter your app secret"
                  value={settings.facebook_app_secret || ''}
                  onChange={(e) => setSettings({ ...settings, facebook_app_secret: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showSecret ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-600">Keep this secret - never share publicly</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Setup Instructions:</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Create app at developers.facebook.com</li>
                <li>2. Add OAuth Redirect: https://photo-ai-album.preview.emergentagent.com/connect/facebook</li>
                <li>3. Request permissions: user_photos, user_posts</li>
                <li>4. Copy App ID and Secret here</li>
              </ol>
            </div>
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-amber-600 to-rose-500 text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
